import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  RideWiseRow,
  TaxiCallerRow,
  CrmAccountRow,
  CrmApprovalRow,
} from "./types";
import {
  buildExpectedCostCode,
  extractTmClientNumberFromCostCode,
  fmtNum,
  getCostCodePrefix,
  isTotalMobilityCostCode,
  normalizeClientNumber,
  safeArr,
} from "./helpers";
import { formatPassengerName } from "@/lib/passengerName";

interface Issue {
  clientNumber: string;
  ridewiseName: string;
  expectedCostCode: string;
  taxicallerCostCode: string | null;
  duplicateCount: number;
  hoistTrips: number;
  trips: number;
  reason: string;
  severity: "high" | "med" | "low";
  hasCrm: boolean;
}

interface Props {
  rw: RideWiseRow[];
  tc: TaxiCallerRow[];
  accounts: CrmAccountRow[];
  approvals: CrmApprovalRow[];
  loading: boolean;
}

const TaxiCallerSetupCheckTab: React.FC<Props> = ({ rw, tc, accounts, approvals, loading }) => {
  const [q, setQ] = useState("");
  const [sev, setSev] = useState<"all" | "high" | "med" | "low">("all");

  const issues = useMemo(() => {
    const list: Issue[] = [];

    const accountsByNum = new Map<string, CrmAccountRow[]>();
    for (const a of safeArr(accounts)) {
      const num = extractTmClientNumberFromCostCode(a.default_cost_code);
      if (!num) continue;
      if (!accountsByNum.has(num)) accountsByNum.set(num, []);
      accountsByNum.get(num)!.push(a);
    }

    const approvalsByNum = new Map<string, CrmApprovalRow[]>();
    for (const a of safeArr(approvals)) {
      const num = extractTmClientNumberFromCostCode(a.cost_code);
      if (!num) continue;
      if (!approvalsByNum.has(num)) approvalsByNum.set(num, []);
      approvalsByNum.get(num)!.push(a);
    }

    const tcByNum = new Map<string, TaxiCallerRow[]>();
    for (const t of safeArr(tc)) {
      if (!isTotalMobilityCostCode(t.Cost_Code)) continue;
      const num = extractTmClientNumberFromCostCode(t.Cost_Code);
      if (!num) continue;
      if (!tcByNum.has(num)) tcByNum.set(num, []);
      tcByNum.get(num)!.push(t);
    }

    interface Agg { trips: number; hoist: number; name: string; }
    const rwByNum = new Map<string, Agg>();
    for (const r of safeArr(rw)) {
      const num = normalizeClientNumber(r.client_number);
      if (!num) continue;
      const cur = rwByNum.get(num) || { trips: 0, hoist: 0, name: "" };
      cur.trips += 1;
      if (r.hoist_deployed) cur.hoist += 1;
      if (!cur.name) cur.name = [r.client_first_name, r.client_surname].filter(Boolean).join(" ");
      rwByNum.set(num, cur);
    }

    for (const [num, agg] of rwByNum.entries()) {
      const expected = buildExpectedCostCode(num, agg.hoist > 0);
      const tcMatches = tcByNum.get(num) || [];
      const tcCC = tcMatches[0]?.Cost_Code ?? null;
      const accs = accountsByNum.get(num) || [];

      // missing in TaxiCaller
      if (!tcCC && agg.trips > 0) {
        list.push({
          clientNumber: num, ridewiseName: agg.name, expectedCostCode: expected,
          taxicallerCostCode: null, duplicateCount: tcMatches.length,
          hoistTrips: agg.hoist, trips: agg.trips,
          reason: "TaxiCaller cost code missing — set up in TaxiCaller using the expected code.",
          severity: "high", hasCrm: accs.length > 0,
        });
        continue;
      }

      if (!tcCC) continue;
      const tcNum = extractTmClientNumberFromCostCode(tcCC);
      const expectedPrefix = agg.hoist > 0 ? "TMH" : "TM";
      const actualPrefix = getCostCodePrefix(tcCC);

      if (tcNum !== num) {
        list.push({
          clientNumber: num, ridewiseName: agg.name, expectedCostCode: expected,
          taxicallerCostCode: tcCC, duplicateCount: tcMatches.length,
          hoistTrips: agg.hoist, trips: agg.trips,
          reason: `Client number mismatch — TaxiCaller has ${tcNum}, RideWise expects ${num}.`,
          severity: "high", hasCrm: accs.length > 0,
        });
      } else if (actualPrefix !== expectedPrefix) {
        list.push({
          clientNumber: num, ridewiseName: agg.name, expectedCostCode: expected,
          taxicallerCostCode: tcCC, duplicateCount: tcMatches.length,
          hoistTrips: agg.hoist, trips: agg.trips,
          reason: `Cost code prefix mismatch — actual is ${actualPrefix}, expected ${expectedPrefix} (hoist=${agg.hoist > 0 ? "yes" : "no"}).`,
          severity: "med", hasCrm: accs.length > 0,
        });
      } else if (tcMatches.length > 1) {
        list.push({
          clientNumber: num, ridewiseName: agg.name, expectedCostCode: expected,
          taxicallerCostCode: tcCC, duplicateCount: tcMatches.length,
          hoistTrips: agg.hoist, trips: agg.trips,
          reason: `${tcMatches.length} TaxiCaller cost code variations found for this client.`,
          severity: "low", hasCrm: accs.length > 0,
        });
      }
    }

    // TaxiCaller TMClient codes that have NO matching ridewise client and NO CRM account
    for (const [num, matches] of tcByNum.entries()) {
      if (rwByNum.has(num)) continue;
      const accs = accountsByNum.get(num) || [];
      if (accs.length > 0) continue;
      list.push({
        clientNumber: num,
        ridewiseName: formatPassengerName(matches[0]?.Passenger_First_Name, matches[0]?.Passenger_Last_Name),
        expectedCostCode: matches[0]?.Cost_Code || "",
        taxicallerCostCode: matches[0]?.Cost_Code || null,
        duplicateCount: matches.length,
        hoistTrips: 0, trips: 0,
        reason: "TaxiCaller TMClient code exists but no RideWise trips and no CRM account.",
        severity: "med", hasCrm: false,
      });
    }

    return list.sort((a, b) => {
      const order = { high: 0, med: 1, low: 2 } as const;
      return order[a.severity] - order[b.severity] || b.trips - a.trips;
    });
  }, [rw, tc, accounts, approvals]);

  const filtered = useMemo(() =>
    issues.filter((i) => {
      if (sev !== "all" && i.severity !== sev) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        if (
          !i.clientNumber.includes(s) &&
          !i.ridewiseName.toLowerCase().includes(s) &&
          !(i.taxicallerCostCode || "").toLowerCase().includes(s)
        ) return false;
      }
      return true;
    }), [issues, q, sev]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading TaxiCaller setup check…</CardContent>
      </Card>
    );
  }

  const counts = {
    total: issues.length,
    high: issues.filter((i) => i.severity === "high").length,
    med: issues.filter((i) => i.severity === "med").length,
    low: issues.filter((i) => i.severity === "low").length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Total Issues</div><div className="text-xl font-bold">{fmtNum(counts.total)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">High</div><div className="text-xl font-bold text-red-500">{fmtNum(counts.high)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Medium</div><div className="text-xl font-bold text-yellow-400">{fmtNum(counts.med)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Low</div><div className="text-xl font-bold text-blue-400">{fmtNum(counts.low)}</div></CardContent></Card>
      </div>

      <Card className="bg-card border border-border">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">TaxiCaller Setup Issues</CardTitle>
            <div className="flex items-center gap-2">
              {(["all", "high", "med", "low"] as const).map((s) => (
                <Button key={s} size="sm" variant={sev === s ? "default" : "outline"} onClick={() => setSev(s)}>
                  {s.toUpperCase()}
                </Button>
              ))}
              <Input
                placeholder="Search…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="dark-input max-w-xs"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {!filtered.length ? (
            <div className="py-12 text-center text-muted-foreground">No setup issues — looking good.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left p-2">Severity</th>
                  <th className="text-left p-2">Client #</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-right p-2">Trips</th>
                  <th className="text-right p-2">Hoist</th>
                  <th className="text-left p-2">Expected CC</th>
                  <th className="text-left p-2">Actual CC</th>
                  <th className="text-left p-2">CRM?</th>
                  <th className="text-left p-2">Reason / Fix</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 1000).map((i, idx) => (
                  <tr key={`${i.clientNumber}-${idx}`} className="border-b border-border/50">
                    <td className="p-2">
                      {i.severity === "high" && <Badge className="bg-red-700">High</Badge>}
                      {i.severity === "med" && <Badge className="bg-yellow-700">Med</Badge>}
                      {i.severity === "low" && <Badge className="bg-blue-700">Low</Badge>}
                    </td>
                    <td className="p-2 font-mono">{i.clientNumber}</td>
                    <td className="p-2">{i.ridewiseName || "—"}</td>
                    <td className="p-2 text-right">{fmtNum(i.trips)}</td>
                    <td className="p-2 text-right">{fmtNum(i.hoistTrips)}</td>
                    <td className="p-2 font-mono">{i.expectedCostCode || "—"}</td>
                    <td className="p-2 font-mono">{i.taxicallerCostCode || "—"}</td>
                    <td className="p-2">
                      {i.hasCrm
                        ? <Badge className="bg-green-700">Yes</Badge>
                        : <Badge className="bg-red-700">No</Badge>}
                    </td>
                    <td className="p-2">{i.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxiCallerSetupCheckTab;
